import React, { useMemo, useState, useEffect } from "react";
import ReusableTable from "../components/Table/ReusableTable";
import Button from "../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { post } from "../utils/api";
import { ArrowLeft, Trash2, Users, Briefcase, Package, IndianRupee } from "lucide-react";
import CustomDialog from "../components/dialog/CustomDialog";
import KpiCard from "../components/Dashboard/KpiCard";

const AdminDashboard = () => {
  const userData = JSON.parse(localStorage.getItem("user_details") || "{}");

  const [activeView, setActiveView] = useState("main");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [selectedDateRange, setSelectedDateRange] = useState({
    from_date: "",
    to_date: "",
    display: "",
  });
  const [searchText, setSearchText] = useState("");
  const [filteredCommissionData, setFilteredCommissionData] = useState([]);
  const [filteredTransactionData, setFilteredTransactionData] = useState([]);

  const [commissionData, setCommissionData] = useState([]);
  const [transactionData, setTransactionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [monthsList, setMonthsList] = useState([]);

  const [utrDialogVisible, setUtrDialogVisible] = useState(false);
  const [selectedRowForUtr, setSelectedRowForUtr] = useState(null);
  const [utrValue, setUtrValue] = useState("");

  const generateMonthsList = () => {
    const now = new Date();
    const months = [];

    months.push({
      value: "current",
      label: `${now.toLocaleString("default", {
        month: "long",
      })} ${now.getFullYear()}`,
    });

    for (let i = 1; i <= 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;
      const label = `${date.toLocaleString("default", {
        month: "long",
      })} ${date.getFullYear()}`;

      months.push({ value, label });
    }

    return months;
  };

  const getMonthDateRange = (monthOption) => {
    const now = new Date();
    let year, month;

    if (monthOption === "current") {
      year = now.getFullYear();
      month = now.getMonth();
    } else {
      [year, month] = monthOption.split("-").map(Number);
      month = month - 1;
    }

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      from_date: formatDate(firstDay),
      to_date: formatDate(lastDay),
      display:
        monthOption === "current"
          ? `${now.toLocaleString("default", {
            month: "long",
          })} ${now.getFullYear()}`
          : `${new Date(year, month).toLocaleString("default", {
            month: "long",
          })} ${year}`,
    };
  };

  const fetchCommissionData = async (monthOption) => {
    setLoading(true);
    setError(null);

    try {
      const dateRange = getMonthDateRange(monthOption);

      setSelectedDateRange(dateRange);

      const payload = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        // ...(userData.pan_number?.trim() ? { admin_pan: userData.pan_number.trim() } : {})
      };

      const response = await post(
        "/admindashboard/fetchDistributorTransactionDetails",
        payload
      );

      if (response.data.Success) {
        const apiData = response.data.Data[0]?.table_details || [];

        const transformedData = apiData.map((item, index) => ({
          id: item.id,
          name: item.distributor_name,
          arn: item.distributor_arn_code,
          distributorPan: item.distributor_pan_number,
          processMonth: item.process_month,
          productCode: item.product_code,
          totalCommission: parseFloat(item.total_commission) || 0,
          gst: parseFloat(item.gst) || 0,
          tds: parseFloat(item.tds) || 0,
          netCommission: parseFloat(item.net_commission_payable) || 0,
          utrNo: item.utr_number,
          invoiceNo: item.invoice_number,
        }));

        setCommissionData(transformedData);

        const filtered = filterData(transformedData);
        setFilteredCommissionData(filtered);
      } else {
        throw new Error(response.data.Message || "Failed to fetch data");
      }
    } catch (err) {
      console.error("Error fetching commission data:", err);
      setError(err.message || "An error occurred while fetching data");
      setCommissionData([]);
      setFilteredCommissionData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactionData = async (
    distributorArn,
    distributorPan,
    month
  ) => {
    setLoading(true);
    setError(null);

    try {
      const dateRange = getMonthDateRange(month);

      const payload = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
        ...(distributorArn ? { distributor_arn_code: distributorArn } : {}),
        ...(distributorPan ? { distributor_pan: distributorPan } : {}),
      };

      const response = await post(
        "/admindashboard/viewDistributorTransactions",
        payload
      );

      if (response.data.Success) {
        const apiData = response.data.Data || [];

        const transformedData = apiData.map((item, index) => ({
          id: item.id,
          transactionDate: item.transaction_date,
          processMonth: item.process_month,
          clientName: item.client_name,
          pan: item.pan_number,
          folioNumber: item.folio_no,
          distributorCode: item.distributor_arn_code,
          distributorPan: item.distributor_pan_number,
          productCode: item.product_code,
          capitalCommitment: parseFloat(item.capital_commitment) || 0,
          placementFeePercent: parseFloat(item.placement_fee_per) || 0,
          placementFeeAmount: parseFloat(item.placement_fee_amount) || 0,
          upfrontFeePercent: parseFloat(item.upfront_fee_per) || 0,
          upfrontFeeAmount: parseFloat(item.upfront_fee_amount) || 0,
          trailPercent: parseFloat(item.trail_per) || 0,
          totalTrailAmount: parseFloat(item.total_trail_amount) || 0,
          trailStartMonth: item.trail_start_month,
          trailEndMonth: item.trail_end_month,
          monthlyTrailAmount: parseFloat(item.monthly_trail_amount) || 0,
          totalCommission: parseFloat(item.total_commission) || 0,
          gst: parseFloat(item.gst) || 0,
          tds: parseFloat(item.tds) || 0,
          statusId: item.status_id || "N/A",
          netCommissionPayable: parseFloat(item.net_commission_payable) || 0,
          remarks: item.uploaded_remarks || item.check_remarks || "",
          isNotCorrect: item.status_id === 3, // Assuming status_id 3 means "not correct"
          utrNumber: item.utr_number,
          invoiceNumber: item.invoice_number, // <-- Add this line
        }));

        setTransactionData(transformedData);

        const filtered = filterData(transformedData);
        setFilteredTransactionData(filtered);

      } else {
        throw new Error(
          response.data.Message || "Failed to fetch transaction data"
        );
      }
    } catch (err) {
      console.error("Error fetching transaction data:", err);
      setError(
        err.message || "An error occurred while fetching transaction data"
      );
      setTransactionData([]);
      setFilteredTransactionData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const months = generateMonthsList();
    setMonthsList(months);
    fetchCommissionData(selectedMonth);
  }, [filteredTransactionData]);

  const handleMonthChange = (e) => {
    const monthOption = e.target.value;
    setSelectedMonth(monthOption);
    fetchCommissionData(monthOption);
  };

  useEffect(() => {
    if (selectedTransaction && activeView === "transactionDetails") {
      fetchTransactionData(
        selectedTransaction.arn,
        selectedTransaction.distributorPan,
        selectedMonth
      );
    }
  }, [selectedTransaction, activeView, selectedMonth]);

  const handleUtrUpdate = (row) => {
    setSelectedRowForUtr(row);
    setUtrValue(row.utrNo || "");
    setUtrDialogVisible(true);
  };


  const handleDeleteTransaction = async (transactionId) => {
    console.log(transactionId, 'id');


    try {
      const response = await post("/admindashboard/deleteTransaction", { id: transactionId });

      if (response.data.Success) {
        // Remove the deleted transaction from state
        const updatedTransactions = transactionData.filter(
          transaction => transaction.id !== transactionId
        );

        setTransactionData(updatedTransactions);

        // Update filtered data
        const filtered = filterData(updatedTransactions);
        setFilteredTransactionData(filtered);

        toast.success("Transaction deleted successfully");
        // window.location.reload();
        setActiveView("main");
      } else {
        toast.error(response.data.Message || "Failed to delete transaction");
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      toast.error("An error occurred while deleting the transaction");
    }
  };

  const kpis = useMemo(() => {
    const totalDistributors = commissionData.length;
    const totalCommission = commissionData.reduce((sum, item) => sum + (item.totalCommission || 0), 0);
    const netCommission = commissionData.reduce((sum, item) => sum + (item.netCommission || 0), 0);
    const pendingRequests = commissionData.filter(item => !item.utrNo).length;

    return {
      totalDistributors,
      totalCommission: totalCommission.toLocaleString("en-IN"),
      netCommission: netCommission.toLocaleString("en-IN"),
      pendingRequests,
    };
  }, [commissionData]);

  // Main table columns
  const columns = useMemo(
    () => [
      { Header: "Distributor Name", accessor: "name", size: "180px" },
      { Header: "Distributor Code (APRN)", accessor: "arn", size: "220px" },
      { Header: "Process Month", accessor: "processMonth", size: "200px" },
      { Header: "Product Code", accessor: "productCode", size: "200px" },
      {
        Header: "Total Commission",
        accessor: "totalCommission",
        size: "200px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "GST",
        accessor: "gst",
        size: "120px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "TDS",
        accessor: "tds",
        size: "80px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "Net Commission",
        accessor: "netCommission",
        size: "200px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      { Header: "UTR No", accessor: "utrNo", size: "120px" },
      { Header: "Invoice No", accessor: "invoiceNo", size: "150px" },
      {
        Header: "Action",
        accessor: "action",
        size: "350px",
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setSelectedTransaction(row.original);
                setActiveView("transactionDetails");
                setSearchText(""); // clear search when switching view
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-lg shadow-md shadow-indigo-100 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5 text-[9px] font-bold uppercase tracking-wider"
            >
              View Transaction
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleUtrUpdate(row.original)}
              className="border-gray-200 hover:bg-indigo-50 hover:text-indigo-600 text-gray-600 px-3 py-1 rounded-lg transition-all duration-300 flex items-center gap-2 shadow-sm text-[9px] font-bold uppercase tracking-wider"
            >
              UTR Update
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  // Transaction table columns
  const transactionColumns = useMemo(
    () => [
      {
        Header: "Transaction Date",
        accessor: "transactionDate",
        size: "200px",
      },
      { Header: "Process Month", accessor: "processMonth", size: "200px" },
      { Header: "Client Name", accessor: "clientName", size: "200px" },
      { Header: "PAN", accessor: "pan", size: "100px" },
      { Header: "Folio Number", accessor: "folioNumber", size: "200px" },
      {
        Header: "Distributor Code (APRN)",
        accessor: "distributorCode",
        size: "220px",
      },
      { Header: "Distributor PAN", accessor: "distributorPan", size: "120px" },
      { Header: "Product Code", accessor: "productCode", size: "100px" },
      {
        Header: "Capital Commitment",
        accessor: "capitalCommitment",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "Placement Fee %",
        accessor: "placementFeePercent",
        size: "180px",
      },
      {
        Header: "Placement Fee Amount",
        accessor: "placementFeeAmount",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      { Header: "Upfront Fee %", accessor: "upfrontFeePercent", size: "180px" },
      {
        Header: "Upfront Fee Amount",
        accessor: "upfrontFeeAmount",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      { Header: "Trail %", accessor: "trailPercent", size: "180px" },
      {
        Header: "Total Trail Amount",
        accessor: "totalTrailAmount",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "Trail Start Month",
        accessor: "trailStartMonth",
        size: "180px",
      },
      { Header: "Trail End Month", accessor: "trailEndMonth", size: "180px" },
      {
        Header: "Monthly Trail Amount",
        accessor: "monthlyTrailAmount",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "Total Commission",
        accessor: "totalCommission",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "GST",
        accessor: "gst",
        size: "80px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "TDS",
        accessor: "tds",
        size: "80px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      {
        Header: "Net Commission Payable",
        accessor: "netCommissionPayable",
        size: "180px",
        Cell: ({ value }) => value.toLocaleString("en-IN"),
      },
      { Header: "Remarks", accessor: "remarks", size: "150px" },
      {
        Header: "Is Not Correct",
        accessor: "isNotCorrect",
        size: "120px",
        Cell: ({ row }) => {
          const rowData = row.original;
          const statusId = rowData.statusId;

          if (statusId === 5) {
            return (
              <span className="text-red-600 font-medium">
                Distributor Marked Not Correct
              </span>
            );
          }

          return null;
        },
      },
      {
        Header: "Delete Transaction",
        accessor: "delete",
        size: "120px",
        Cell: ({ row }) => (
          <button
            className="text-red-500 hover:text-red-700 p-1"
            onClick={() => handleDeleteTransaction(row.original.id)}
          >
            <Trash2 size={18} />
          </button>
        ),
      },
    ],
    []
  );

  const filterData = (data) => {
    if (!searchText.trim()) return data;

    const lowerSearch = searchText.toLowerCase();

    return data.filter((row) =>
      Object.values(row).some((val) => {
        if (val === null || val === undefined) return false;
        if (typeof val === "boolean")
          return val.toString().toLowerCase().includes(lowerSearch);
        return val.toString().toLowerCase().includes(lowerSearch);
      })
    );
  };

  useEffect(() => {
    const filtered = filterData(commissionData);
    setFilteredCommissionData(filtered);
  }, [commissionData, searchText]);

  useEffect(() => {
    if (!selectedTransaction) {
      setFilteredTransactionData([]);
      return;
    }
    const filtered = filterData(transactionData);
    setFilteredTransactionData(filtered);
  }, [transactionData, searchText]);

  const exportToExcel = () => {
    let dataToExport = [];
    let fileName = "";

    if (activeView === "main") {
      dataToExport = filteredCommissionData.map(({ id, ...rest }) => rest);
      fileName = `CommissionDetails_${selectedDateRange.display.replace(
        " ",
        "_"
      )}.xlsx`;
    } else if (activeView === "transactionDetails") {
      dataToExport = filteredTransactionData.map(({ id, ...rest }) => rest);
      fileName = `TransactionDetails_${selectedTransaction?.name
        }_${selectedDateRange.display.replace(" ", "_")}.xlsx`;
    }

    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/octet-stream",
    });
    saveAs(blob, fileName);
  };

  const saveUtrUpdate = async () => {
    if (!selectedRowForUtr || !utrValue.trim()) {
      toast.error("UTR number cannot be empty.");
      return;
    }

    try {
      const payload = {
        utr_number: utrValue.trim(),
        distributor_arn_code: selectedRowForUtr.arn,
        product_code: selectedRowForUtr.productCode,
        process_month: selectedRowForUtr.processMonth,
      };

      console.log(payload, "pay");

      const response = await post(
        "/admindashboard/updateInvoiceUTRNumber",
        payload
      );

      if (response.data.Success) {
        const updatedData = commissionData.map((item) =>
          item.id === selectedRowForUtr.id ? { ...item, utrNo: utrValue } : item
        );

        setCommissionData(updatedData);

        const filtered = filterData(updatedData);
        setFilteredCommissionData(filtered);

        toast.success("UTR updated successfully.");
        setUtrDialogVisible(false);
      } else {
        toast.error(response.data.Message || "Failed to update UTR.");
      }
    } catch (error) {
      console.error("UTR update error:", error);
      toast.error("An error occurred while updating the UTR number.");
    }
  };

  if (loading) {
    return (
      <div className="bg-[#f4f3ef] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-overview">
      <div className="admin-overview-header">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-gray-800 tracking-tight">
            {activeView === "main" ? "Commission Dashboard" : "Transaction Details"}
          </h1>
          <p className="text-[11px] text-gray-400 font-medium tracking-wide">
            {activeView === "main"
              ? "Monitor and manage distributor commissions"
              : `Viewing transactions for ${selectedTransaction?.name}`}
          </p>
        </div>
        <div className="flex gap-3">
          {activeView !== "main" && (
            <Button
              variant="outline"
              size="md"
              onClick={() => {
                setActiveView("main");
                setSearchText("");
              }}
              className="border-gray-100 hover:bg-gray-50 hover:text-black text-gray-600 px-5 py-2 rounded-full transition-all duration-300 flex items-center gap-2 shadow-sm text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <Button
            variant="default"
            size="md"
            onClick={exportToExcel}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg shadow-lg shadow-indigo-200 transition-all duration-300 flex items-center gap-2 transform hover:-translate-y-0.5"
          >
            <Package className="w-4 h-4 mr-2" />
            Export to Excel
          </Button>
        </div>
      </div>

      {activeView === "main" && (
        <div className="admin-kpi-grid">
          <KpiCard
            title="Total Distributors"
            value={kpis.totalDistributors}
            subtitle="Active this month"
            icon={<Users size={20} />}
            iconBgColor="#EEF2FF"
            iconColor="#6366F1"
          />
          <KpiCard
            title="Total Commission"
            value={`₹${kpis.totalCommission}`}
            subtitle="Gross amount"
            icon={<IndianRupee size={20} />}
            iconBgColor="#F5F3FF"
            iconColor="#7C3AED"
          />
          <KpiCard
            title="Net Payable"
            value={`₹${kpis.netCommission}`}
            subtitle="After GST & TDS"
            icon={<Briefcase size={20} />}
            iconBgColor="#FFF7ED"
            iconColor="#EA580C"
          />
          <KpiCard
            title="Pending Payments"
            value={kpis.pendingRequests}
            subtitle="Awaiting UTR"
            icon={<Package size={20} />}
            iconBgColor="#ECFDF5"
            iconColor="#059669"
          />
        </div>
      )}

      <div className="dash-card p-6 bg-white rounded-xl shadow-sm border border-gray-100">
        {activeView === "main" ? (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="relative w-full max-w-sm">
                <input
                  type="text"
                  placeholder="Search distributors, APRN..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="flex items-center gap-3">
                <label htmlFor="month-select" className="text-sm font-semibold text-gray-600">
                  Month
                </label>
                <select
                  id="month-select"
                  value={selectedMonth}
                  className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 transition-all"
                  onChange={handleMonthChange}
                >
                  {monthsList.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">Error: {error}</p>
                  </div>
                </div>
              </div>
            )}

            <ReusableTable
              columns={columns}
              data={filteredCommissionData}
              emptyMessage={`No commission data found for ${selectedDateRange.display}`}
            />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-800">
                Data for {selectedDateRange.display}
              </h3>
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                <p className="text-sm text-red-700">Error: {error}</p>
              </div>
            )}

            <ReusableTable
              columns={transactionColumns}
              data={filteredTransactionData}
            />
          </div>
        )}
      </div>

      <CustomDialog
        visible={utrDialogVisible}
        onHide={() => setUtrDialogVisible(false)}
        header="UTR Update"
        width="50vw"
      >
        {selectedRowForUtr && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Distributor Name
                </label>
                <input
                  type="text"
                  value={selectedRowForUtr.name}
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Distributor Code (APRN)
                </label>
                <input
                  type="text"
                  value={selectedRowForUtr.arn}
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Process Month
                </label>
                <input
                  type="text"
                  value={selectedRowForUtr.processMonth}
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Code
                </label>
                <input
                  type="text"
                  value={selectedRowForUtr.productCode}
                  className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  readOnly
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  UTR Number
                </label>
                <input
                  type="text"
                  value={utrValue}
                  onChange={(e) => setUtrValue(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Enter UTR Number"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="cancel"
                onClick={() => setUtrDialogVisible(false)}
                className="px-5 py-2 font-bold"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={saveUtrUpdate}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2 rounded-xl shadow-lg shadow-indigo-100 transition-all transform hover:-translate-y-0.5 font-bold"
              >
                Save Changes
              </Button>
            </div>
          </div>
        )}
      </CustomDialog>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default AdminDashboard;